import React, { useEffect, useState } from 'react';
import { CampaignPackage, listPackages } from '../../api/packages';

const CheckoutSummary: React.FC<{
  items?: { name: string; price: number; quantity: number }[];
  totalAmount?: number;
  onConfirm?: () => void;
  onCancel?: () => void;
}> = ({ items = [], totalAmount = 0 }) => {
  const [packages, setPackages] = useState<CampaignPackage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const fetchPackages = async () => {
      if (!totalAmount || totalAmount <= 0) {
        setPackages([]);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const result = await listPackages(totalAmount);
        if (!cancelled) {
          setPackages(result);
        }
      } catch (err) {
        if (!cancelled) {
          const message = err instanceof Error ? err.message : 'Failed to load packages';
          setError(message);
          setPackages([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchPackages();
    return () => {
      cancelled = true;
    };
  }, [totalAmount]);

  const effectiveItems =
    items.length > 0
      ? items
      : packages.map((pkg) => ({ name: pkg.name, price: pkg.basePrice, quantity: 1 }));
  const effectiveTotal =
    totalAmount || effectiveItems.reduce((sum, item) => sum + item.price * item.quantity, 0);

  if (!effectiveItems.length) {
    return (
      <div className="space-y-1 text-xs text-slate-600 dark:text-slate-400">
        <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Campaign package</h2>
        {loading && <p>Loading packages…</p>}
        {!loading && !error && (
          <p>Enter an amount to preview a suggested TELLURVISION campaign mix.</p>
        )}
        {error && <p className="text-red-600">{error}</p>}
      </div>
    );
  }

  return (
    <div className="space-y-2 text-xs text-slate-700 dark:text-slate-300">
      <h2 className="text-sm font-semibold text-slate-900 dark:text-slate-100">Campaign package</h2>
      <ul className="space-y-1">
        {effectiveItems.map((item, index) => (
          <li key={index} className="flex items-center justify-between">
            <span>{item.name}</span>
            <span className="font-medium">${item.price.toFixed(2)}</span>
          </li>
        ))}
      </ul>
      <div className="mt-1 flex items-center justify-between border-t border-slate-200 dark:border-slate-700 pt-1 text-[11px]">
        <span className="font-medium text-slate-800 dark:text-slate-200">Estimated budget</span>
        <span className="font-semibold text-slate-900 dark:text-slate-100">${effectiveTotal.toFixed(2)}</span>
      </div>
    </div>
  );
};

export default CheckoutSummary;
